import { QueryKey } from '@/configs/queryKeys';
import { CreatorProductModule } from './CreatorProductModuleApi';
import { Attachment, UUID } from '@/types/types';
import { SerializedCreatorState } from '@/types/creator';
import EntityApi, { BaseEntity } from './EntityApi';
import ProductModuleCollectionApi from './ProductModuleCollectionApi';
import AttributeValueApi from './AttributeValueApi';
import ProductModuleApi from './ProductModuleApi';
import httpClient from './httpClient';
import PresetApi from './PresetApi';

export interface Project extends BaseEntity {
	creatorProductModules: CreatorProductModule[];
	price: number;
	image?: Attachment;
	serializedConfiguration?: SerializedCreatorState;
}

export type LoadedCreatorData = Awaited<
	ReturnType<ProjectApi['loadCreatorData']>
>;

class ProjectApi extends EntityApi<Project> {
	constructor() {
		super(QueryKey.projects);
		this.loadCreatorData = this.loadCreatorData.bind(this);
		this.clear = this.clear.bind(this);
		this.createFromPreset = this.createFromPreset.bind(this);
		this.exportFile = this.exportFile.bind(this);
		this.setCreatorProductModulesOrder =
			this.setCreatorProductModulesOrder.bind(this);
	}

	private async enrichCreatorProductModule(
		creatorProductModule: CreatorProductModule
	) {
		const attributeValues = await AttributeValueApi.mutationFindByIds(
			creatorProductModule.selectedAttributeValuesIds
		);
		creatorProductModule.selectedAttributeValues = attributeValues;

		const productModule = await ProductModuleApi.mutationGetOne(
			creatorProductModule.productModuleId
		);
		creatorProductModule.productModule = productModule;

		return creatorProductModule;
	}

	private async enrichCreatorProductModules(project: Project) {
		await Promise.all(
			project.creatorProductModules.map(this.enrichCreatorProductModule)
		);
	}

	async loadCreatorData(projectId: UUID) {
		const project = await super.mutationGetOne(projectId);

		await this.enrichCreatorProductModules(project);

		const serializedConfiguration = project.serializedConfiguration!;

		const requests = [
			ProductModuleCollectionApi.mutationGetOne(
				serializedConfiguration.selectedProductModuleCollectionId
			),
		];

		const [productModuleCollection] = await Promise.all(requests);

		return {
			project,
			productModuleCollection,
			preset: serializedConfiguration.selectedPreset,
			globalSelectedAttributeValues:
				serializedConfiguration.globalSelectedAttributeValuesIds,
			serializedRenderer: serializedConfiguration.serializedRenderer,
			options: serializedConfiguration.options,
		};
	}

	async clear(id: UUID) {
		const response = await httpClient.patch<Project>(
			`${this.queryKey}/${id}/clear`
		);
		return response.data;
	}

	async createFromPreset({
		presetId,
		projectId,
	}: {
		presetId: UUID;
		projectId: UUID;
	}) {
		const preset = await PresetApi.mutationGetOne(presetId);

		const response = await httpClient.patch<Project>(
			`${this.queryKey}/${projectId}/from-preset`,
			{
				preset: {
					id: presetId,
				},
			}
		);

		const project = response.data;
		await this.enrichCreatorProductModules(project);

		return { project, preset };
	}

	async exportFile({ id, type }: { id: UUID; type: 'pdf' | 'xlsx' }) {
		const response = await httpClient.get<Blob>(
			`${this.queryKey}/${id}/export/${type}`,
			{
				responseType: 'blob',
			}
		);

		return response.data;
	}

	async setCreatorProductModulesOrder({
		projectId,
		listOrder,
	}: {
		projectId: UUID;
		listOrder: UUID[];
	}) {
		await httpClient.patch<void>(
			`${this.queryKey}/${projectId}/order-modules`,
			{
				listOrder,
			}
		);
	}
}

export default new ProjectApi();
